import type { Condo, CondoImage } from '~/core/entities/condo/Condo';
import type { SectionName } from '~/types/mixpanel';
import type { VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'vue';

import { cva } from 'class-variance-authority';

export { default as CondoCard } from './Card.vue';
export { default as CondoCardHeader } from './CondoCardHeader.vue';
export { default as CondoCardFooter } from './CondoCardFooter.vue';

export type CondoCardProps = {
	condo: Condo;
	class?: HTMLAttributes['class'];
	size?: CondoCardBodyVariants['size'];
	variant?: CondoCardBodyVariants['variant'];
	cardPosition?: number;
	section?: SectionName;
	titleAs?: string;
};

export interface CarouselItem extends CondoImage {}

export interface CondoCardHeaderProps extends Pick<
	CondoCardProps,
	'size' | 'variant' | 'cardPosition' | 'section' | 'class' | 'section'
> {
	isMouseOverCardHeader?: boolean;
}

export interface CondoCardFooterProps extends Pick<
	CondoCardProps,
	'size' | 'variant' | 'class' | 'titleAs'
> {}

export const condoCardBodyVariants = cva('relative w-full', {
	variants: {
		variant: {
			whiteBackground: 'border border-white-dark-3'
		},
		size: {
			sm: '',
			md: '',
			lg: ''
		}
	},
	defaultVariants: {
		size: 'md',
		variant: 'whiteBackground'
	}
});

export type CondoCardBodyVariants = VariantProps<typeof condoCardBodyVariants>;

export const condoCardHeaderBodyVariants = cva(
	'relative w-full overflow-hidden p-0',
	{
		variants: {
			variant: {
				whiteBackground: ''
			},
			size: {
				sm: 'h-[180px] md:h-[256px]',
				md: 'h-[220px] md:h-[256px] md:min-h-[256px]',
				lg: 'h-[365px]'
			}
		},
		defaultVariants: {
			size: 'md',
			variant: 'whiteBackground'
		}
	}
);

export type CondoCardHeaderBodyVariants = VariantProps<
	typeof condoCardHeaderBodyVariants
>;

export const condoCardFooterBodyVariants = cva('relative w-full', {
	variants: {
		variant: {
			whiteBackground: ''
		},
		size: {
			sm: '',
			md: 'flex flex-col gap-2 p-4',
			lg: ''
		}
	},
	defaultVariants: {
		size: 'md',
		variant: 'whiteBackground'
	}
});

export const condoCardFooterNameVariants = cva('', {
	variants: {
		variant: {
			whiteBackground: 'font-matter-sq font-normal text-primary'
		},
		size: {
			sm: '',
			md: 'text-xl leading-[150%] tracking-[-0.36px]',
			lg: ''
		}
	},
	defaultVariants: {
		size: 'md',
		variant: 'whiteBackground'
	}
});

export const condoCardFooterAddressVariants = cva('', {
	variants: {
		variant: {
			whiteBackground: 'truncate font-medium text-primary-light-3'
		},
		size: {
			sm: '',
			md: 'text-xs leading-[150%] md:text-sm md:leading-5',
			lg: ''
		}
	},
	defaultVariants: {
		size: 'md',
		variant: 'whiteBackground'
	}
});

export const condoCardFooterTypesVariants = cva('', {
	variants: {
		variant: {
			whiteBackground: 'font-normal text-primary-light-3'
		},
		size: {
			sm: '',
			md: 'text-xs leading-[150%]',
			lg: ''
		}
	},
	defaultVariants: {
		size: 'md',
		variant: 'whiteBackground'
	}
});

export const cardCarouselDotsVariants = cva(
	'absolute inset-x-0 mx-auto flex h-5 w-fit gap-2 rounded-full bg-primary/40 px-3 py-2  transition-all delay-300 ease-in-out',
	{
		variants: {
			size: {
				sm: 'bottom-[10px]',
				md: 'bottom-[10px] md:bottom-[10px]',
				lg: 'bottom-[10px] xl:bottom-[10px]'
			},
			variant: {
				fullFace: 'opacity-100'
			}
		},
		defaultVariants: {
			size: 'sm'
		}
	}
);

export type CardCarouselDotsVariants = VariantProps<
	typeof cardCarouselDotsVariants
>;

export const condoCardCarouselDotsVariants = cva(
	'absolute inset-x-0 mx-auto flex h-5 w-fit gap-2 rounded-full bg-primary/40 px-3 py-2  transition-all delay-300 ease-in-out',
	{
		variants: {
			size: {
				sm: 'bottom-2',
				md: 'bottom-2',
				lg: 'bottom-2'
			},
			variant: {}
		},
		defaultVariants: {
			size: 'md'
		}
	}
);

export type CondoCardCarouselDotsVariants = VariantProps<
	typeof condoCardCarouselDotsVariants
>;
